import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  Loader2,
  Users,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  Star,
  Layers,
  Eye
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { renderSafeHtml, stripHtml } from '../components/RichTextEditor';

const StudentAttempts = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { token, isTeacher } = useAuth();
  const [student, setStudent] = useState(null);
  const [examGroups, setExamGroups] = useState([]);
  const [topicGroups, setTopicGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attemptDetail, setAttemptDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [examSessionDetail, setExamSessionDetail] = useState(null);
  const [examDetailLoading, setExamDetailLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isTeacher) {
      navigate('/teacher/login');
      return;
    }
    fetchData();
  }, [isTeacher, studentId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentRes, attemptsRes, topicAttemptsRes] = await Promise.all([
        fetch(`${API_URL}/api/teacher/students/${studentId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/teacher/students/${studentId}/exam-attempts`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/teacher/students/${studentId}/topic-attempts`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (studentRes.ok) setStudent(await studentRes.json());
      if (attemptsRes.ok) setExamGroups(await attemptsRes.json());
      else toast.error('فشل تحميل محاولات الاختبارات');
      if (topicAttemptsRes.ok) setTopicGroups(await topicAttemptsRes.json());
    } catch {
      toast.error('حدث خطأ أثناء التحميل');
    }
    setLoading(false);
  };

  const openExamSessionDetail = async (sessionId) => {
    setExamDetailLoading(true);
    setExamSessionDetail({ loading: true });
    try {
      const r = await fetch(`${API_URL}/api/teacher/exam-sessions/${sessionId}/detail`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) setExamSessionDetail(await r.json());
      else { setExamSessionDetail(null); toast.error('فشل تحميل تفاصيل الجلسة'); }
    } catch { setExamSessionDetail(null); toast.error('حدث خطأ'); }
    setExamDetailLoading(false);
  };

  const openTopicAttemptDetail = async (attemptId) => {
    setDetailLoading(true);
    setAttemptDetail({ loading: true });
    try {
      const r = await fetch(`${API_URL}/api/teacher/topic-attempts/${attemptId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setAttemptDetail(data);
      } else {
        setAttemptDetail(null);
        toast.error('فشل تحميل تفاصيل المحاولة');
      }
    } catch {
      setAttemptDetail(null);
      toast.error('حدث خطأ');
    }
    setDetailLoading(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">مكتمل</Badge>;
      case 'terminated':
        return <Badge variant="destructive">منتهي بمخالفة</Badge>;
      case 'in_progress':
        return <Badge variant="secondary">جارٍ</Badge>;
      case 'blocked':
        return <Badge className="bg-orange-100 text-orange-800">محجوب</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#EF9F27]" />
      </div>
    );
  }

  const totalAttempts = examGroups.reduce((acc, g) => acc + g.attempts.length, 0);
  const totalTopicAttempts = topicGroups.reduce((acc, g) => acc + g.attempts.length, 0);

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="bg-[#FFFFFF] border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/teacher/dashboard" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#E8DFCF]" />
              <span className="text-xl font-bold text-[#EF9F27]">المعتمد في اللغة العربية</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/teacher/dashboard')}
          className="text-[#6B4C2A] hover:text-[#412402] mb-4"
        >
          <ArrowRight className="w-4 h-4 ml-2" />
          العودة للوحة التحكم
        </Button>

        {/* Student Info */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#412402] flex items-center gap-2">
            <Users className="w-6 h-6 text-[#EF9F27]" />
            جميع محاولات الطالب
          </h1>
          {student && (
            <p className="text-[#6B4C2A] mt-1">
              {student.full_name || student.name} — {student.grade} / شعبة {student.section}
              &nbsp;·&nbsp;@{student.username}
            </p>
          )}
          <p className="text-sm text-[#412402]/50 mt-1">
            محاولات الاختبارات: {totalAttempts} · محاولات بنك الأسئلة: {totalTopicAttempts}
          </p>
        </div>

        <Tabs defaultValue="exams" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
            <TabsTrigger value="exams">الاختبارات ({examGroups.length})</TabsTrigger>
            <TabsTrigger value="topics">بنك الأسئلة ({topicGroups.length})</TabsTrigger>
          </TabsList>

        <TabsContent value="exams">
        {examGroups.length === 0 ? (
          <Card className="border-[#E8DFCF]">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-[#412402]/30 mx-auto mb-4" />
              <p className="text-[#412402]/60">لا توجد محاولات لهذا الطالب بعد</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {examGroups.map((group) => (
              <Card key={group.exam_id} className="border-[#E8DFCF]">
                <CardHeader className="border-b border-[#E8DFCF] pb-3">
                  <CardTitle className="text-base font-semibold text-[#412402] flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#EF9F27]" />
                      {group.exam_title}
                    </span>
                    <span className="text-sm font-normal text-[#412402]/50">
                      {group.attempts.length} محاولة · {group.total_points} درجة
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                      <thead>
                        <tr className="bg-[#FAFAFA] border-b border-[#E8DFCF]">
                          <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">المحاولة</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">الحالة</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">الدرجة</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">المخالفات</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">وقت البدء</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">وقت الانتهاء</th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-[#412402]/60">تفاصيل</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.attempts
                          .slice()
                          .sort((a, b) => a.attempt_number - b.attempt_number)
                          .map((attempt) => {
                            const pct = group.total_points
                              ? Math.round(((attempt.score || 0) / group.total_points) * 100)
                              : 0;
                            const passed = pct >= 50;
                            return (
                              <tr
                                key={attempt.session_id}
                                className={`border-b border-[#E8DFCF] ${attempt.is_first_attempt ? 'bg-amber-50/40' : 'hover:bg-[#FAFAFA]'}`}
                              >
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    <span className="text-sm font-medium text-[#412402]">
                                      #{attempt.attempt_number}
                                    </span>
                                    {attempt.is_first_attempt && (
                                      <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                        <Star className="w-3 h-3" />
                                        الدرجة الرسمية
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3">{getStatusBadge(attempt.status)}</td>
                                <td className="px-4 py-3">
                                  {attempt.score !== null && attempt.score !== undefined ? (
                                    <div className="flex items-center gap-2">
                                      <span className={`font-bold ${passed ? 'text-green-600' : 'text-red-600'}`}>
                                        {attempt.score}
                                      </span>
                                      <span className="text-[#412402]/40 text-xs">/ {group.total_points}</span>
                                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {pct}%
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-[#412402]/40 text-sm">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {attempt.violation_count > 0 ? (
                                    <Badge variant="destructive" className="text-xs">{attempt.violation_count}</Badge>
                                  ) : (
                                    <span className="text-[#412402]/40">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-xs text-[#6B4C2A]">
                                  {new Date(attempt.started_at).toLocaleString('ar-SA')}
                                </td>
                                <td className="px-4 py-3 text-xs text-[#6B4C2A]">
                                  {attempt.ended_at
                                    ? new Date(attempt.ended_at).toLocaleString('ar-SA')
                                    : '—'}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <Button size="sm" variant="ghost" onClick={() => openExamSessionDetail(attempt.session_id)}>
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </TabsContent>

        <TabsContent value="topics">
          {topicGroups.length === 0 ? (
            <Card className="border-[#E8DFCF]">
              <CardContent className="py-12 text-center">
                <Layers className="w-12 h-12 text-[#412402]/30 mx-auto mb-4" />
                <p className="text-[#412402]/60">لا توجد محاولات على بنك الأسئلة لهذا الطالب بعد</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {topicGroups.map((group) => (
                <Card key={group.topic_id} className="border-[#E8DFCF]">
                  <CardHeader className="border-b border-[#E8DFCF] pb-3">
                    <CardTitle className="text-base font-semibold text-[#412402] flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-[#EF9F27]" />
                        {group.topic_title}
                      </span>
                      <span className="text-sm font-normal text-[#412402]/50">
                        {group.attempts.length} محاولة · {group.questions_per_test} سؤال/اختبار · {group.total_bank_questions} في البنك
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px]">
                        <thead>
                          <tr className="bg-[#FAFAFA] border-b border-[#E8DFCF]">
                            <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">المحاولة</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">الحالة</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">الدرجة</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">المخالفات</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">وقت البدء</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-[#412402]/60">وقت الانتهاء</th>
                            <th className="px-4 py-2 text-center text-xs font-semibold text-[#412402]/60">تفاصيل</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.attempts
                            .slice()
                            .sort((a, b) => a.attempt_number - b.attempt_number)
                            .map((attempt) => {
                              const pct = attempt.total_points
                                ? Math.round(((attempt.score || 0) / attempt.total_points) * 100)
                                : 0;
                              const passed = pct >= 50;
                              return (
                                <tr key={attempt.attempt_id} className="border-b border-[#E8DFCF] hover:bg-[#FAFAFA]">
                                  <td className="px-4 py-3">
                                    <span className="text-sm font-medium text-[#412402]">#{attempt.attempt_number}</span>
                                  </td>
                                  <td className="px-4 py-3">{getStatusBadge(attempt.status)}</td>
                                  <td className="px-4 py-3">
                                    {attempt.score !== null && attempt.score !== undefined ? (
                                      <div className="flex items-center gap-2">
                                        <span className={`font-bold ${passed ? 'text-green-600' : 'text-red-600'}`}>{attempt.score}</span>
                                        <span className="text-[#412402]/40 text-xs">/ {attempt.total_points}</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{pct}%</span>
                                      </div>
                                    ) : (
                                      <span className="text-[#412402]/40 text-sm">—</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    {attempt.violation_count > 0 ? (
                                      <Badge variant="destructive" className="text-xs">{attempt.violation_count}</Badge>
                                    ) : (<span className="text-[#412402]/40">—</span>)}
                                  </td>
                                  <td className="px-4 py-3 text-xs text-[#6B4C2A]">{new Date(attempt.started_at).toLocaleString('ar-SA')}</td>
                                  <td className="px-4 py-3 text-xs text-[#6B4C2A]">
                                    {attempt.ended_at ? new Date(attempt.ended_at).toLocaleString('ar-SA') : '—'}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <Button size="sm" variant="ghost" onClick={() => openTopicAttemptDetail(attempt.attempt_id)} data-testid={`view-topic-attempt-${attempt.attempt_id}`}>
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        </Tabs>
      </main>

      {/* Exam Session Detail Dialog */}
      <Dialog open={!!examSessionDetail} onOpenChange={(o) => { if (!o) setExamSessionDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">تفاصيل جلسة الاختبار</DialogTitle>
          </DialogHeader>
          {examDetailLoading || examSessionDetail?.loading ? (
            <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#EF9F27] mx-auto" /></div>
          ) : examSessionDetail && examSessionDetail.session_id ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 rounded p-3 text-center">
                  <p className="text-xs text-[#412402]/60">الدرجة</p>
                  <p className="text-lg font-bold text-[#412402]">{examSessionDetail.score ?? '—'}/{examSessionDetail.total_points}</p>
                </div>
                <div className="bg-green-50 rounded p-3 text-center">
                  <p className="text-xs text-[#412402]/60">صحيحة</p>
                  <p className="text-lg font-bold text-green-700">{(examSessionDetail.questions || []).filter(q => q.is_correct).length}</p>
                </div>
                <div className="bg-red-50 rounded p-3 text-center">
                  <p className="text-xs text-[#412402]/60">خاطئة</p>
                  <p className="text-lg font-bold text-red-700">{(examSessionDetail.questions || []).filter(q => !q.is_correct && q.student_answer).length}</p>
                </div>
                <div className="bg-amber-50 rounded p-3 text-center">
                  <p className="text-xs text-[#412402]/60">المخالفات</p>
                  <p className="text-lg font-bold text-amber-700">{examSessionDetail.violation_count}</p>
                </div>
              </div>
              {examSessionDetail.violations?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2 text-red-700">المخالفات المسجلة:</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {examSessionDetail.violations.map(v => (
                      <div key={v.id} className="text-xs flex items-center gap-2 bg-red-50 rounded p-2">
                        <AlertTriangle className="w-3 h-3 text-red-600" />
                        <span className="text-red-700">#{v.violation_number} {v.violation_type}</span>
                        <span className="text-[#412402]/50 mr-auto">{new Date(v.timestamp).toLocaleTimeString('ar-SA')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">الأسئلة:</h4>
                {(examSessionDetail.questions || []).map((q, idx) => (
                  <div key={q.id} className={`border rounded p-3 ${q.is_correct ? 'bg-green-50/50' : q.student_answer ? 'bg-red-50/50' : 'bg-amber-50/50'}`}>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-bold">س{idx + 1}</span>
                      <Badge variant="outline" className="text-xs">{q.difficulty}</Badge>
                      {q.is_correct ? <Badge className="bg-green-100 text-green-700 text-xs">صحيحة</Badge>
                        : q.student_answer ? <Badge className="bg-red-100 text-red-700 text-xs">خاطئة</Badge>
                        : <Badge className="bg-amber-100 text-amber-700 text-xs">لم يجب</Badge>}
                    </div>
                    <p className="text-sm text-[#412402] mb-2" dangerouslySetInnerHTML={renderSafeHtml(q.text)} />
                    <div className="text-xs space-y-1">
                      {q.question_type === 'multiple_choice' && (q.options || []).length > 0 && (
                        <>
                          <p>إجابة الطالب: <span className={q.is_correct ? 'text-green-700 font-bold' : 'text-red-700'}>{stripHtml(q.options.find(o => o.id === q.student_answer)?.text || '') || '—'}</span></p>
                          <p>الإجابة الصحيحة: <span className="text-green-700 font-bold">{stripHtml(q.options.find(o => o.id === q.correct_answer)?.text || '') || q.correct_answer}</span></p>
                        </>
                      )}
                      {q.question_type === 'true_false' && (
                        <>
                          <p>إجابة الطالب: <span className={q.is_correct ? 'text-green-700 font-bold' : 'text-red-700'}>{q.student_answer === 'true' ? 'صح' : q.student_answer === 'false' ? 'خطأ' : '—'}</span></p>
                          <p>الإجابة الصحيحة: <span className="text-green-700 font-bold">{q.correct_answer === 'true' ? 'صح' : 'خطأ'}</span></p>
                        </>
                      )}
                      {q.question_type === 'short_answer' && (
                        <>
                          <p>إجابة الطالب: <span className={q.is_correct ? 'text-green-700 font-bold' : 'text-red-700'}>{q.student_answer || '—'}</span></p>
                          <p>الإجابة الصحيحة: <span className="text-green-700 font-bold">{q.correct_answer}</span></p>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Topic Attempt Detail Dialog */}
      <Dialog open={!!attemptDetail} onOpenChange={(o) => { if (!o) setAttemptDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">تفاصيل المحاولة</DialogTitle>
          </DialogHeader>
          {detailLoading || attemptDetail?.loading ? (
            <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#EF9F27] mx-auto" /></div>
          ) : attemptDetail && attemptDetail.attempt_id ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 rounded p-3 text-center">
                  <p className="text-xs text-[#412402]/60">الدرجة</p>
                  <p className="text-lg font-bold text-[#412402]">{attemptDetail.score ?? '—'}/{attemptDetail.total_points}</p>
                </div>
                <div className="bg-green-50 rounded p-3 text-center">
                  <p className="text-xs text-[#412402]/60">صحيحة</p>
                  <p className="text-lg font-bold text-green-700">{attemptDetail.questions.filter(q => q.is_correct).length}</p>
                </div>
                <div className="bg-red-50 rounded p-3 text-center">
                  <p className="text-xs text-[#412402]/60">خاطئة</p>
                  <p className="text-lg font-bold text-red-700">{attemptDetail.questions.filter(q => !q.is_correct && q.student_answer).length}</p>
                </div>
                <div className="bg-amber-50 rounded p-3 text-center">
                  <p className="text-xs text-[#412402]/60">المخالفات</p>
                  <p className="text-lg font-bold text-amber-700">{attemptDetail.violation_count}</p>
                </div>
              </div>

              {attemptDetail.violations && attemptDetail.violations.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2 text-red-700">المخالفات المسجلة:</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {attemptDetail.violations.map(v => (
                      <div key={v.id} className="text-xs flex items-center gap-2 bg-red-50 rounded p-2">
                        <AlertTriangle className="w-3 h-3 text-red-600" />
                        <span className="text-red-700">#{v.violation_number} {v.violation_type}</span>
                        <span className="text-[#412402]/50 mr-auto">{new Date(v.timestamp).toLocaleTimeString('ar-SA')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <h4 className="font-semibold text-sm">الأسئلة:</h4>
                {attemptDetail.questions.map((q, idx) => (
                  <div key={q.id} className={`border rounded p-3 ${q.is_correct ? 'bg-green-50/50' : q.student_answer ? 'bg-red-50/50' : 'bg-amber-50/50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold">س{idx + 1}</span>
                      <Badge variant="outline" className="text-xs">{q.difficulty}</Badge>
                      {q.is_correct ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">صحيحة</Badge>
                      ) : q.student_answer ? (
                        <Badge className="bg-red-100 text-red-700 text-xs">خاطئة</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">لم يجب</Badge>
                      )}
                    </div>
                    <p className="text-sm text-[#412402] mb-2" dangerouslySetInnerHTML={renderSafeHtml(q.text)} />
                    <div className="text-xs space-y-1">
                      {q.question_type === 'multiple_choice' && (q.options || []).length > 0 && (
                        <>
                          <p>إجابة الطالب: <span className={q.is_correct ? 'text-green-700 font-bold' : 'text-red-700'}>
                            {stripHtml(q.options.find(o => o.id === q.student_answer)?.text || '') || '—'}
                          </span></p>
                          <p>الإجابة الصحيحة: <span className="text-green-700 font-bold">
                            {stripHtml(q.options.find(o => o.id === q.correct_answer)?.text || '') || q.correct_answer}
                          </span></p>
                        </>
                      )}
                      {q.question_type === 'true_false' && (
                        <>
                          <p>إجابة الطالب: <span className={q.is_correct ? 'text-green-700 font-bold' : 'text-red-700'}>
                            {q.student_answer === 'true' ? 'صح' : q.student_answer === 'false' ? 'خطأ' : '—'}
                          </span></p>
                          <p>الإجابة الصحيحة: <span className="text-green-700 font-bold">
                            {q.correct_answer === 'true' ? 'صح' : 'خطأ'}
                          </span></p>
                        </>
                      )}
                      {q.question_type === 'short_answer' && (
                        <>
                          <p>إجابة الطالب: <span className={q.is_correct ? 'text-green-700 font-bold' : 'text-red-700'}>{q.student_answer || '—'}</span></p>
                          <p>الإجابة الصحيحة: <span className="text-green-700 font-bold">{q.correct_answer}</span></p>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentAttempts;
